import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import * as yup from "yup"
import { useAuthControllerLogoutMutation } from "~/ui-rtk/api/authApi"
import { CompanyMetadataDto } from "~/ui-rtk/api/types"
import {
  SOS_FREE_MAX_CATEGORIES,
  SOS_FREE_MAX_COMPETITORS,
} from "~/ui-rtk/constants/sos"
import useAccess from "~/ui-rtk/hooks/access"
import { useCompanyMetadataUpdater } from "~/ui-rtk/hooks/company-updater"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  brandsIncludeOwnTerm,
  mergeAliasIntoBrands,
  ownBrandTermMissingMessage,
  resolveOwnBrandTerm,
} from "~/ui-rtk/utils/keywords"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"
import { LocalStorageKeys, useLocalStorage } from "~/ui-rtk/utils/storage"

export type TMindShareSettingsFormValues = {
  googleSearchAlias: string | undefined
  googleCompetitors: string[]
  googleCategories: string[]
}

// Paid companies stay unconstrained; the free SoS tier gets the same 5+5
// caps the signup wizard enforces (constants/sos.ts) — every keyword-config
// change resets the connector's sync hash and re-triggers a full backfill on
// the shared Google Ads quota, so unbounded edits are a real cost (APP-452).
//
// The dialog has no Brand Keywords field. Free-tier saves merge the alias into
// brands[] themselves; paid saves leave brands[] alone, so a paid alias that
// brands[] does not carry would silently drop the company's own brand from the
// report (MD-5463). Validate it and point at the page that can fix it.
export const buildValidationSchema = (
  isSosFree: boolean,
  ownBrand?: { companyName?: string; brands: string[] },
) =>
  yup.object().shape({
    googleSearchAlias:
      !isSosFree && ownBrand
        ? yup
            .string()
            .optional()
            .test("own-brand-in-brands", function (alias) {
              return brandsIncludeOwnTerm(
                alias,
                ownBrand.companyName,
                ownBrand.brands,
              )
                ? true
                : this.createError({
                    message: `${ownBrandTermMissingMessage(alias, ownBrand.companyName)} Add it on Setup > Keywords + Competitors.`,
                  })
            })
        : yup.string().optional(),
    googleCompetitors: isSosFree
      ? yup
          .array()
          .of(yup.string())
          .max(
            SOS_FREE_MAX_COMPETITORS,
            `The free plan includes up to ${SOS_FREE_MAX_COMPETITORS} competitors`,
          )
      : yup.array().of(yup.string()),
    // .min(1, "At least one competitor is required")
    // .required("Please fill keywords"),
    googleCategories: isSosFree
      ? yup
          .array()
          .of(yup.string())
          .max(
            SOS_FREE_MAX_CATEGORIES,
            `The free plan includes up to ${SOS_FREE_MAX_CATEGORIES} categories`,
          )
      : yup.array().of(yup.string()),
    // .min(1, "At least one category is required")
    // .required("Please fill keywords"),
  })

export default function useConnect() {
  const [dontShowDialogAgain, setDontShowDialogAgain] =
    useLocalStorage<boolean>(
      LocalStorageKeys.LOCAL_STORAGE_DONT_SHOW_AGAIN_SAVE_KEYWORD_DIALOG_NOTIFICATON_KEY,
      false,
    )
  const [reloaded, triggerReload] = useState(false)
  const currentCompany = useCurrentCompany()
  const { isSosFree } = useAccess()
  const { keywords } = (currentCompany?.metadata ?? {}) as {
    keywords: CompanyMetadataDto["keywords"]
  }
  const validationSchema = useMemo(
    () =>
      buildValidationSchema(isSosFree, {
        companyName: currentCompany?.name,
        brands: keywords?.google?.brands ?? [],
      }),
    [isSosFree, currentCompany?.name, keywords?.google?.brands],
  )
  const { updateCompanyMetadata } = useCompanyMetadataUpdater()
  const [logout] = useAuthControllerLogoutMutation()
  const navigate = useStableNavigate()

  const initialValues: TMindShareSettingsFormValues = {
    googleSearchAlias: keywords?.google?.searchAlias,
    googleCompetitors: keywords?.google?.competitors ?? [],
    googleCategories: keywords?.google?.categories ?? [],
  }

  const setDontShowDialogAgainHandler = (val: boolean) => {
    setDontShowDialogAgain(val)
    triggerReload(!reloaded)
  }

  const updateKeywords = async ({
    googleSearchAlias,
    googleCompetitors,
    googleCategories,
  }: TMindShareSettingsFormValues) => {
    if (!currentCompany) {
      return
    }
    const metadata: CompanyMetadataDto = {
      ...(currentCompany?.metadata ?? {}),
      keywords: {
        ...(currentCompany?.metadata?.keywords ?? {}),
        google: {
          ...(currentCompany?.metadata?.keywords?.google ?? {
            hash: null,
            brands: [],
            regions: [],
          }),
          searchAlias: googleSearchAlias,
          // Free tier: brands[] is exactly the one brand term — dbt drops the
          // company's own brand rows unless the alias (or company name when
          // the alias is cleared) appears there. Stale extras from before the
          // single-keyword policy are dropped on save. Paid companies curate
          // brands[] themselves — it feeds branded-search classification.
          brands: isSosFree
            ? mergeAliasIntoBrands(
                resolveOwnBrandTerm(googleSearchAlias, currentCompany.name),
                [],
              )
            : (currentCompany?.metadata?.keywords?.google?.brands ?? []),
          competitors: googleCompetitors,
          categories: googleCategories,
        },
      },
    }

    try {
      await updateCompanyMetadata(metadata)
      if (dontShowDialogAgain) {
        toast.success("Keywords were updated")
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(error)
      if (error.status === 401) {
        await logout()
        navigate("/")
        return
      }
      toast.error("Could not update keywords")
    }
  }

  return {
    companyName: currentCompany?.name,
    initialValues,
    validationSchema,
    updateKeywords,
    dontShowDialogAgain,
    setDontShowDialogAgain: setDontShowDialogAgainHandler,
  }
}
